import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Dialog, Typography } from "@mui/material";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { COLORS } from "./constants";

interface PackReturnModalProps {
  open: boolean;
  onClose: () => void;
  socket: any;
  shopId: any;
  onReturned: () => void;
}

// Pack Return popup (Figma node 2346:146). Returns a confirmed/active pack by
// scan or manual game + pack entry. Mirrors the ActivateGameModal shell (orange
// header, orange divider, navy footer) but without the slot field. Stays open
// after each return so several packs can be returned back to back.
const PackReturnModal: React.FC<PackReturnModalProps> = ({ open, onClose, socket, shopId, onReturned }) => {
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  const [returning, setReturning] = React.useState<boolean>(false);

  // Guards against returning the same scanned ticket twice (a scanner fires a
  // burst of key events plus an Enter terminator).
  const processedScanRef = React.useRef<string>("");

  const clearFields = () => {
    setScanCode("");
    setGameNumber("");
    setPackNumber("");
    processedScanRef.current = "";
  };

  React.useEffect(() => {
    if (!socket) return;
    const onSuccess = () => {
      setReturning(false);
      showSuccessMessage(t("packReturnSuccessMsg", "Pack returned successfully"));
      clearFields();
      onReturned();
    };
    const onError = (response: any) => {
      setReturning(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };
    socket.on("packReturnInventoryV2Response", onSuccess);
    socket.on("packReturnInventoryV2Error", onError);
    return () => {
      socket.off("packReturnInventoryV2Response", onSuccess);
      socket.off("packReturnInventoryV2Error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Return a pack. Takes explicit game/pack so a scan can submit freshly parsed
  // values without waiting for input state to update.
  const submitPackReturn = (game: string, pack: string) => {
    if (!socket) {
      return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    }
    if (!/^\d{4}$/.test(game)) {
      return showErrorMessage(t("gameNumberFourDigitsError", "The game number must be 4 digits."));
    }
    if (!/^\d{7}$/.test(pack)) {
      return showErrorMessage(t("packNumberSevenDigitsError", "The pack number should be 7 digits."));
    }
    setReturning(true);
    socket.emit("packReturnInventoryV2", { shopId, gameNumber: game, packNumber: pack });
  };

  const handlePackReturn = () => submitPackReturn(gameNumber, packNumber);

  const processScan = (code: string) => {
    const game = code.substring(0, 4);
    const pack = code.substring(4, 11);
    const key = `${game}-${pack}`;
    if (key === processedScanRef.current) return;
    processedScanRef.current = key;
    setGameNumber(game);
    setPackNumber(pack);
    submitPackReturn(game, pack);
  };

  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length < 14) processedScanRef.current = "";
    if (value.length >= 11) {
      setGameNumber(value.substring(0, 4));
      setPackNumber(value.substring(4, 11));
    }
    if (value.length >= 14) processScan(value);
  };

  const onScanKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const code = scanCode.trim();
    if (code.length >= 11) processScan(code);
  };

  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!returning) handlePackReturn();
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const labelSx = { fontSize: "18px", color: "#434343", mb: "6px" } as const;
  const inputSx = {
    width: "100%",
    height: "40px",
    borderRadius: "10px",
    border: `1px solid ${COLORS.border}`,
    backgroundColor: "#fff",
    px: "12px",
    outline: "none",
    fontSize: "17px",
  } as const;
  const footerBtnSx = {
    backgroundColor: COLORS.navy,
    color: "#fff",
    textTransform: "none",
    borderRadius: "2px",
    px: 3,
    fontSize: "16px",
    fontWeight: 600,
    letterSpacing: "1px",
    "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 },
  } as const;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // popup only closes via the explicit Close / x controls
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
      PaperProps={{
        sx: {
          width: "505px",
          maxWidth: "95vw",
          borderRadius: "10px",
          border: `2px solid ${COLORS.orange}`,
          backgroundColor: COLORS.panelBg,
          overflow: "hidden",
        },
      }}
    >
      {/* Orange header */}
      <Box sx={{ backgroundColor: COLORS.orange, height: "43px", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Typography sx={{ color: "#fff", fontSize: "22px", fontWeight: 600, letterSpacing: "1px" }}>
          {t("packReturnTitle", "Pack Return")}
        </Typography>
        <Box component='span' onClick={handleClose} sx={{ color: "#fff", fontSize: "26px", cursor: "pointer", lineHeight: 1 }} aria-label={t("close", "Close")}>
          ×
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Box mb={1.5}>
          <Typography sx={labelSx}>{t("scanCodeLabel", "Scan Code")}</Typography>
          <Box component='input' value={scanCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)} onKeyDown={onScanKeyDown} sx={inputSx} />
        </Box>
        {/* Orange divider separating scan entry from manual entry (Figma node 2346:146) */}
        <Box sx={{ height: "2px", backgroundColor: COLORS.orange, mx: -1.5, mb: 2.5 }} />
        <Box mb={2}>
          <Typography sx={labelSx}>{t("gameNumberLabel", "Game Number")}</Typography>
          <Box component='input' value={gameNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={inputSx} />
        </Box>
        <Box mb={1}>
          <Typography sx={labelSx}>{t("packNumberLabel", "Pack Number")}</Typography>
          <Box component='input' value={packNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={inputSx} />
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${COLORS.orange}`, display: "flex", justifyContent: "flex-end", gap: "12px", p: 2 }}>
        <Button onClick={handleClose} disableElevation sx={footerBtnSx}>
          {t("close", "Close")}
        </Button>
        <Button onClick={handlePackReturn} disabled={returning} disableElevation sx={footerBtnSx}>
          {t("packReturnButton", "Pack Return")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default PackReturnModal;
